/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  addCompetitor,
  addProductType,
  confirmProductType as confirmProductTypeCall,
  createReport,
  getReport,
  getSetupStep,
  getValidation,
  listCompetitors,
  setCompetitorStatus,
  isUsageLimitError,
  startRun,
  updateReport,
} from './competitiveIntelApi';
import { CI_REPORT_STATUS } from './ciV2Realtime';

/**
 * Competitive Intel part of an Intelligence Report draft (store key `ReportCompetitiveIntel`).
 *
 * It drives the Competitive Intel V2 setup (handoff FE_HANDOFF_V2_ENDPOINTS.md §4) for the wizard:
 *   1. POST reports (or PATCH reports/{id} with a new product) → status pending → validating
 *   2. wait for status "validated" — `.report.updated` / GET reports/{id} (CompetitiveIntelDraftListener)
 *   3. GET reports/{id}/validation                → product types (wizard step 2)
 *   3a. POST reports/{id}/product-types           → a setup step, followed until it settles
 *   4. PATCH reports/{id}/validation              → status discovering → wait for "discovered"
 *   5. GET reports/{id}/competitors               → competitors (wizard step 3)
 *   5a. PATCH reports/{id}/competitors/{cid}      → keep ('active') / reject ('rejected')
 *   5b. POST reports/{id}/competitors             → a setup step, followed until it settles
 *   6. POST reports/{id}/runs                     → start: nothing runs before this (wizard submit)
 * Wizard step 4 starts the run, then submits the whole report (POST /intel-reports,
 * IntelligenceReport.slice) with this report's id as `competitiveIntelRequestId`. A draft left any
 * other way is only cleared here: the backend takes care of reports left incomplete.
 * The socket / polling half (steps 2 and 4) lives in CompetitiveIntelDraftListener.
 */

export const CI_PHASE = {
  IDLE: 'idle',
  VALIDATING: 'validating',
  VALIDATED: 'validated',
  DISCOVERING: 'discovering',
  DISCOVERED: 'discovered',
  FAILED: 'failed',
};

export const CI_VALIDATION_TYPE = { PRODUCT_TYPE: 'product_type', COMPETITOR: 'competitor' };

// A typed product type / competitor is checked in 8–15 s (handoff §4); give up after 2 minutes.
const VALIDATION_TIMEOUT_MS = 120000;
// The setup step is read back over HTTP too, in case its `.setup-step.updated` signal is missed.
const VALIDATION_POLL_MS = 5000;

const FAILED_REPORT_MESSAGE = 'We could not check this product. Please try again.';

const idleValidation = { type: null, value: '', status: 'idle', error: '' };

const initialState = {
  requestId: null,
  productKey: null,
  phase: CI_PHASE.IDLE,
  // Set once create/update (or the type confirmation) has answered, so the listener only reads
  // statuses that belong to the current step.
  awaitingStatus: false,
  failedPhase: null,
  statusMessage: '',
  error: '',
  productTypes: { validatedName: '', recommended: '', alternatives: [] },
  productType: null,
  competitorsFor: null,
  foundCount: 0,
  // Most competitors a run measures (GET …/competitors `meta.maxActive`); null until read.
  maxCompetitors: null,
  competitors: [],
  validation: idleValidation,
  socketAlive: false,
  finalized: false,
};

const sameName = (a = '', b = '') => a.toLowerCase() === b.toLowerCase();

const toProductTypes = (data = {}) => ({
  validatedName: data.validatedProductName || data.productName || '',
  recommended: data.recommendedProductType || '',
  alternatives: (data.alternativeProductTypes || []).filter(Boolean),
});

// `active` (kept) and `pending` count as selected; `rejected` doesn't.
const toCompetitors = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((competitor) => competitor.status !== 'archived')
    .map((competitor) => ({
      id: competitor.competitorId,
      name: competitor.competitorName,
      selected: competitor.status !== 'rejected',
    }));

const ReportCompetitiveIntelSlice = createSlice({
  name: 'ReportCompetitiveIntel',
  initialState,
  reducers: {
    resetCompetitiveIntelDraft(state) {
      return { ...initialState, socketAlive: state.socketAlive };
    },
    setSocketAlive(state, action) {
      state.socketAlive = action.payload;
    },
    validationRequested(state, action) {
      state.productKey = action.payload;
      state.phase = CI_PHASE.VALIDATING;
      state.awaitingStatus = false;
      state.failedPhase = null;
      state.error = '';
      state.statusMessage = '';
    },
    requestAccepted(state, action) {
      state.requestId = action.payload;
      state.awaitingStatus = true;
    },
    statusMessageChanged(state, action) {
      state.statusMessage = action.payload || '';
    },
    statusSettled(state) {
      state.awaitingStatus = false;
    },
    phaseFailed(state, action) {
      state.failedPhase = action.payload.phase;
      state.phase = CI_PHASE.FAILED;
      state.awaitingStatus = false;
      state.error = action.payload.message;
    },
    productTypesLoaded(state, action) {
      const types = action.payload;
      state.phase = CI_PHASE.VALIDATED;
      state.productTypes = types;
      state.productType = types.recommended || types.alternatives[0] || null;
      state.competitorsFor = null;
    },
    // After a custom type is accepted: keep it selected, appending it if the reload lacks it.
    productTypesRefreshed(state, action) {
      const { types, select } = action.payload;
      const all = [types.recommended, ...types.alternatives].filter(Boolean);
      const match = all.find((option) => sameName(option, select));
      state.productTypes = match
        ? types
        : { ...types, alternatives: [...types.alternatives, select] };
      state.productType = match || select;
    },
    productTypeSelected(state, action) {
      state.productType = action.payload;
    },
    discoveryRequested(state) {
      state.phase = CI_PHASE.DISCOVERING;
      state.awaitingStatus = false;
      state.failedPhase = null;
      state.error = '';
      state.statusMessage = '';
      state.competitorsFor = state.productType;
      state.competitors = [];
    },
    competitorsLoaded(state, action) {
      const list = action.payload;
      state.phase = CI_PHASE.DISCOVERED;
      state.competitors = list;
      state.foundCount = list.length;
    },
    competitorsRefreshed(state, action) {
      state.competitors = action.payload;
    },
    competitorLimitLoaded(state, action) {
      const limit = Number(action.payload);
      state.maxCompetitors = Number.isFinite(limit) && limit > 0 ? limit : null;
    },
    competitorSelectionChanged(state, action) {
      const { id, selected } = action.payload;
      state.competitors = state.competitors.map((c) => (c.id === id ? { ...c, selected } : c));
    },
    validationStarted(state, action) {
      state.validation = { ...idleValidation, ...action.payload, status: 'pending' };
    },
    validationSucceeded(state) {
      state.validation = idleValidation;
    },
    validationFailed(state, action) {
      state.validation = {
        ...state.validation,
        ...action.payload.target,
        status: 'failed',
        error: action.payload.error,
      };
    },
    // The report is now part of a submitted Audit — it must no longer be cancelled.
    markCompetitiveIntelSubmitted(state) {
      state.finalized = true;
    },
  },
});

export const {
  resetCompetitiveIntelDraft,
  setSocketAlive,
  statusMessageChanged,
  statusSettled,
  phaseFailed,
  productTypeSelected,
  markCompetitiveIntelSubmitted,
} = ReportCompetitiveIntelSlice.actions;

const {
  validationRequested,
  requestAccepted,
  productTypesLoaded,
  productTypesRefreshed,
  discoveryRequested,
  competitorsLoaded,
  competitorLimitLoaded,
  competitorsRefreshed,
  competitorSelectionChanged,
  validationStarted,
  validationSucceeded,
  validationFailed,
} = ReportCompetitiveIntelSlice.actions;

const draftOf = (getState) => getState().ReportCompetitiveIntel;
const messageOf = (error, fallback) => error?.message || fallback;

// ── Steps 1–3: create the report (or change its product); the listener then waits ──

/** Step 1: create the V2 report, or point the existing one at a changed product. */
export const startProductValidation = ({ name, url }) => async (dispatch, getState) => {
  const { requestId } = draftOf(getState);
  const key = `${name}|${url}`;
  dispatch(validationRequested(key));
  // The wizard may have been left (draft reset) or the product changed while this was in flight.
  const isStale = () => draftOf(getState).productKey !== key;
  const create = async () => (await createReport({ productName: name, productUrl: url })).data;
  try {
    let report;
    if (requestId) {
      try {
        report = (await updateReport(requestId, { productName: name, productUrl: url })).data;
      } catch (error) {
        // "The product can no longer be changed for this report." → start over with a new one (the
        // backend takes care of the one left behind). Any other refusal ("The product is already
        // being checked…") is shown as it is.
        if (error.status !== 409 || !/no longer be changed/i.test(error.message || '')) throw error;
        report = await create();
      }
    } else {
      report = await create();
    }
    const id = report?.reportId ?? requestId;
    if (!id) throw new Error("We couldn't start the report. Please try again.");
    // The wizard was left or the product changed meanwhile: this report isn't used.
    if (isStale()) return;
    dispatch(requestAccepted(id));
  } catch (error) {
    if (isStale()) return;
    dispatch(
      phaseFailed({
        phase: CI_PHASE.VALIDATING,
        message: messageOf(error, 'We could not start competitive intelligence. Please try again.'),
      })
    );
  }
};

/** Step 3: the product is checked — load it and its types. */
export const loadValidatedProduct = () => async (dispatch, getState) => {
  try {
    const { data } = await getValidation(draftOf(getState).requestId);
    dispatch(productTypesLoaded(toProductTypes(data)));
  } catch (error) {
    dispatch(
      phaseFailed({
        phase: CI_PHASE.VALIDATING,
        message: messageOf(error, "We couldn't load the validated product. Please try again."),
      })
    );
  }
};

// ── Steps 4–5: confirm the type; the listener then waits for "discovered" ──

// Statuses after the type is confirmed: the competitors are being found, or already are.
const DISCOVERY_STATUSES = [CI_REPORT_STATUS.DISCOVERING, CI_REPORT_STATUS.DISCOVERED];

/**
 * PATCH validation answered 409 ("The product type cannot be confirmed now."): the type is already
 * confirmed — the member came back to Step 2 and continued again, or a confirm went through twice.
 * When the confirmed type is the one chosen, carry on from where the report is: wait for discovery,
 * or load the competitors it found. Resolves whether it recovered.
 */
const resumeConfirmedType = (requestId, productType) => async (dispatch) => {
  try {
    const [{ data: report }, { data: validation }] = await Promise.all([
      getReport(requestId),
      getValidation(requestId),
    ]);
    const confirmed =
      validation?.validationStatus === 'confirmed' &&
      sameName(validation?.productType || '', productType || '');
    if (!confirmed || !DISCOVERY_STATUSES.includes(report?.status)) return false;
    if (report.status === CI_REPORT_STATUS.DISCOVERED) {
      // eslint-disable-next-line no-use-before-define
      dispatch(loadCompetitors());
    } else {
      dispatch(requestAccepted(requestId));
    }
    return true;
  } catch {
    return false;
  }
};

/** Step 4: confirm the product type; competitors are then found by themselves. */
export const confirmProductType = () => async (dispatch, getState) => {
  const { requestId, productType } = draftOf(getState);
  dispatch(discoveryRequested());
  try {
    await confirmProductTypeCall(requestId, productType);
    dispatch(requestAccepted(requestId));
  } catch (error) {
    if (error?.status === 409 && (await dispatch(resumeConfirmedType(requestId, productType)))) {
      return;
    }
    dispatch(
      phaseFailed({
        phase: CI_PHASE.DISCOVERING,
        message: messageOf(error, "We couldn't start finding competitors. Please try again."),
      })
    );
  }
};

/** Step 5: discovery finished — load the competitors CI found. */
export const loadCompetitors = () => async (dispatch, getState) => {
  try {
    const { data, meta } = await listCompetitors(draftOf(getState).requestId);
    dispatch(competitorLimitLoaded(meta?.maxActive));
    dispatch(competitorsLoaded(toCompetitors(data)));
  } catch (error) {
    dispatch(
      phaseFailed({
        phase: CI_PHASE.DISCOVERING,
        message: messageOf(error, "We couldn't load the competitors. Please try again."),
      })
    );
  }
};

/**
 * Step 5a: keep / reject a competitor (optimistic). Resolves `{ ok }`, with the backend's sentence
 * when it refuses (409 "A report keeps at most 5 competitors. Reject one before adding another.").
 */
export const setCompetitorSelected = (id, selected) => async (dispatch, getState) => {
  dispatch(competitorSelectionChanged({ id, selected }));
  try {
    await setCompetitorStatus(draftOf(getState).requestId, id, selected ? 'active' : 'rejected');
    return { ok: true };
  } catch (error) {
    dispatch(competitorSelectionChanged({ id, selected: !selected }));
    return { ok: false, message: error?.status === 409 ? error.message : '' };
  }
};

/**
 * GET reports/{id} answered (a signal, a poll, or the first read): move the setup along. Only reads
 * statuses while a step is awaited (`awaitingStatus`).
 */
export const reportStatusReceived = (report) => (dispatch, getState) => {
  const { phase, awaitingStatus } = draftOf(getState);
  if (!awaitingStatus || !report) return;
  const { status } = report;
  if (status === CI_REPORT_STATUS.FAILED || status === CI_REPORT_STATUS.CANCELLED) {
    dispatch(
      phaseFailed({
        phase: phase === CI_PHASE.DISCOVERING ? CI_PHASE.DISCOVERING : CI_PHASE.VALIDATING,
        message: report.setup?.message || report.message || FAILED_REPORT_MESSAGE,
      })
    );
    return;
  }
  if (phase === CI_PHASE.VALIDATING && status === CI_REPORT_STATUS.VALIDATED) {
    dispatch(statusSettled());
    dispatch(loadValidatedProduct());
  } else if (phase === CI_PHASE.DISCOVERING && status === CI_REPORT_STATUS.DISCOVERED) {
    dispatch(statusSettled());
    dispatch(loadCompetitors());
  }
};

// ── Steps 3a / 5b: a typed product type / competitor, checked by CI ──
//
// Two "slots" can wait on CI at once: `manual` (the Add / Define-your-own input) and `typed` (the
// Step 1 Competitor List names CI didn't find, added one by one in the background). Each POST
// answers 202 with a setup step; the slot follows that step — on its `.setup-step.updated` signal or
// a 5 s read — until it's `succeeded` (the value is added: reload and select `acceptedValue`) or
// `rejected` / `failed` / `cancelled` (show the step's `message`). Each slot gives up after 2 minutes.

const MANUAL = 'manual';
const SETUP_STEP_DONE = 'succeeded';
const SETUP_STEP_RUNNING = 'running';
const watches = new Map(); // slot → { type, value, requestId, setupStepId, timer }
let watchPoll = null;

const stopWatchPollIfIdle = () => {
  if (watches.size || !watchPoll) return;
  clearInterval(watchPoll);
  watchPoll = null;
};

const dropWatch = (slot) => {
  const watch = watches.get(slot);
  if (watch) clearTimeout(watch.timer);
  watches.delete(slot);
  stopWatchPollIfIdle();
};

/** Settle one slot: update its UI state, and move the background queue on. */
const resolveWatch = (slot, ok, error = '') => (dispatch) => {
  if (!watches.has(slot)) return;
  dropWatch(slot);
  if (ok) {
    dispatch(validationSucceeded());
  } else {
    dispatch(validationFailed({ error }));
  }
};

/** A setup step read back: settle its slot once the step is no longer running. */
const settleWatch = (slot, watch, step) => async (dispatch) => {
  if (watches.get(slot) !== watch || !step || step.status === SETUP_STEP_RUNNING) return;
  if (step.status !== SETUP_STEP_DONE) {
    dispatch(resolveWatch(slot, false, step.message || 'This could not be added.'));
    return;
  }
  try {
    if (watch.type === CI_VALIDATION_TYPE.PRODUCT_TYPE) {
      const { data } = await getValidation(watch.requestId);
      dispatch(
        productTypesRefreshed({
          types: toProductTypes(data),
          select: step.acceptedValue || watch.value,
        })
      );
    } else {
      const { data, meta } = await listCompetitors(watch.requestId);
      dispatch(competitorLimitLoaded(meta?.maxActive));
      dispatch(competitorsRefreshed(toCompetitors(data)));
    }
    dispatch(resolveWatch(slot, true));
  } catch (error) {
    dispatch(resolveWatch(slot, false, messageOf(error, 'This could not be added.')));
  }
};

/** Read every outstanding setup step and settle the ones that are done. */
const checkWatches = () => (dispatch, getState) => {
  const { requestId } = draftOf(getState);
  [...watches.entries()].forEach(([slot, watch]) => {
    if (watch.requestId !== requestId) {
      dropWatch(slot); // draft reset / left
      return;
    }
    if (watch.setupStepId == null) return;
    getSetupStep(watch.requestId, watch.setupStepId)
      .then(({ data }) => dispatch(settleWatch(slot, watch, data)))
      .catch(() => null); // keep waiting: the next check, a signal or the timeout settles it
  });
};

/** POST the typed value for a slot and follow the setup step it answers with. */
const startWatch = (slot, type, value, requestId) => (dispatch) => {
  const watch = { type, value, requestId, setupStepId: null };
  watches.set(slot, watch);
  const call =
    type === CI_VALIDATION_TYPE.PRODUCT_TYPE
      ? addProductType(requestId, value)
      : addCompetitor(requestId, value);
  call
    .then(({ data }) => {
      if (watches.get(slot) !== watch) return;
      watch.setupStepId = data?.setupStepId ?? null;
      watch.timer = setTimeout(() => {
        if (watches.get(slot) === watch) {
          dispatch(resolveWatch(slot, false, 'This took too long to check. Please try again.'));
        }
      }, VALIDATION_TIMEOUT_MS);
      // The step may already be settled by the time it's read.
      dispatch(settleWatch(slot, watch, data));
      if (!watchPoll) watchPoll = setInterval(() => dispatch(checkWatches()), VALIDATION_POLL_MS);
    })
    .catch((error) => {
      if (watches.get(slot) === watch) {
        dispatch(resolveWatch(slot, false, messageOf(error, 'This could not be added.')));
      }
    });
};

/**
 * Check the value typed in the Add Competitor / Define-your-own input. Always calls the endpoint
 * right away — the background Step 1 names never block it (the backend may answer that another
 * check is still running; that message is shown as it is).
 */
export const requestValidation = (type, value) => (dispatch, getState) => {
  const { requestId, validation } = draftOf(getState);
  if (validation.status === 'pending') return;
  dispatch(validationStarted({ type, value }));
  dispatch(startWatch(MANUAL, type, value, requestId));
};

/** `.setup-step.updated` handler: settle the slot following that step, if any. */
export const setupStepSignalReceived = (setupStepId) => (dispatch) => {
  const entry = [...watches.entries()].find(([, watch]) => watch.setupStepId === setupStepId);
  if (!entry) return;
  const [slot, watch] = entry;
  getSetupStep(watch.requestId, setupStepId)
    .then(({ data }) => dispatch(settleWatch(slot, watch, data)))
    .catch(() => null);
};

// ── Step 6: start the report (wizard submit) ──

/**
 * POST reports/{id}/runs — in V2 nothing runs until this. Resolves `{ ok, message }`; `message` is
 * the backend's sentence when the start is refused (e.g. "Reports cannot be started yet.").
 * `includeHistoricalData` (Step 4 "Include historical data") also builds the longer timeframes.
 */
export const startCompetitiveIntelRun = (options = {}) => async (dispatch, getState) => {
  const { requestId } = draftOf(getState);
  if (!requestId) {
    return { ok: false, message: 'Competitive intelligence isn’t ready yet. Please try again.' };
  }
  try {
    await startRun(requestId, { includeHistoricalData: Boolean(options.includeHistoricalData) });
    return { ok: true };
  } catch (error) {
    // A retried submit after the start already went through.
    if (error.status === 409 && /already running/i.test(error.message || '')) return { ok: true };
    // 403 "Usage limit reached for Competitive Intel…": the plan's analyses are used up.
    if (isUsageLimitError(error)) return { ok: false, message: error.message, usageLimit: true };
    return { ok: false, message: messageOf(error, "We couldn't start the competitive report.") };
  }
};

export default ReportCompetitiveIntelSlice.reducer;
